/**
 * Print the catalogue we sent this buyer.
 *
 * Reads the STAFF endpoint, not `/s/:token`, for two reasons that only matter
 * after the fact: a revoked or expired link answers 410 — and staff most need a
 * record of what was sent once the link is dead — and fetching the token would
 * bump the buyer's view counter, which is how the office reads interest.
 *
 * The prices are the buyer's own: `PrintCatalogueTable` puts every line through
 * the shared `publicPrice`, so a BAND catalogue prints bands and a NONE
 * catalogue prints no price column. `listPriceSnapshot` never reaches paper.
 */
import type { CreatedSelection } from '@paradise/shared';
import { selectionDocRef } from '@paradise/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import {
  useProductItemNoLookup,
  useProductMeasureLookup,
  useProductThumbLookup
} from '@/components/lead-detail/use-product-covers';
import { PrintCatalogueTable } from '@/components/print/print-catalogue-table';
import { documentSubtitle } from '@/components/print/document-date';
import { PrintDocumentPage } from '@/components/print/print-document-page';
import { useLeadQuoteQuery } from '@/components/print/use-lead-quote-query';
import { apiRequest } from '@/lib/api/http';
import { mediaUrl } from '@/lib/media';

export const Route = createFileRoute('/_app/leads/$leadId/print/catalogue/$selectionId')({
  staticData: { hideShellChrome: true },
  component: PrintCatalogueRoute
});

function PrintCatalogueRoute() {
  const { leadId, selectionId } = Route.useParams();
  const { data: leadResult } = useLeadQuoteQuery(leadId);
  const photos = useProductThumbLookup();
  const measures = useProductMeasureLookup();
  const itemNos = useProductItemNoLookup();

  const { data: selection, isPending } = useQuery({
    queryKey: ['selections', selectionId],
    queryFn: async () => {
      const result = await apiRequest<CreatedSelection>(`/api/selections/${selectionId}`);
      return result.kind === 'ok' ? result.data : null;
    },
    staleTime: 30_000
  });

  const lead = leadResult?.lead ?? null;
  const status = isPending ? 'Loading…' : selection === null ? 'That catalogue no longer exists.' : null;

  return (
    <PrintDocumentPage
      title="Catalogue"
      subtitle={
        selection ? documentSubtitle('Catalogue', selectionDocRef(selection.id), selection.createdAt) : 'Catalogue'
      }
      recipient={lead ? { company: lead.company, contactName: lead.contactName } : null}
      leadId={leadId}
      footerRight={selection?.priceMode === 'NONE' ? 'Prices on request' : 'Prices in INR'}
      statusMessage={status}
    >
      {selection && (
        <PrintCatalogueTable
          items={selection.items.map(item => ({
            productId: item.productId,
            productNameSnapshot: item.productNameSnapshot,
            unitPrice: item.unitPrice
          }))}
          priceMode={selection.priceMode}
          currency={lead?.currency ?? 'INR'}
          photos={photos}
          photoUrl={key => mediaUrl('products', key)}
          measures={measures}
          itemNos={itemNos}
        />
      )}
    </PrintDocumentPage>
  );
}
